import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { CountUp } from "../components/CountUp";
import { SEO } from "../components/SEO";
import { SeoBlock } from "../components/SeoBlock";
import { useSiteSettings } from "../lib/settings";
import { useContent } from "../lib/content";
import { resolveImage } from "../lib/realisations";
import { Icon } from "../lib/icons";
import { heroCardStyle } from "../lib/heroCard";

const FALLBACK_TEAM = [
  { name: "Grégoire", role: "Dirigeant", photo: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=800&q=80" },
  { name: "Marie", role: "Économiste", photo: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80" },
  { name: "Julien", role: "Ingénieur OPC", photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80" },
];

export default function Entreprise() {
  const { settings } = useSiteSettings();
  const c = useContent("entreprise");
  const TEAM = settings?.team?.length ? settings.team : FALLBACK_TEAM;
  const values = c.values || [];
  const stats = c.stats || [];

  return (
    <div data-testid="page-entreprise">
      <SEO path="/entreprise" title="L'Entreprise — ECO-GEST PATRIMOINE" description="Découvrez ECO-GEST PATRIMOINE, bureau d'études à Marigny-Saint-Marcel. Une équipe à taille humaine, engagée et expérimentée pour vos projets de construction." />

      <section className="relative h-[50vh] min-h-[360px] bg-[#1A1A1A] text-white overflow-hidden flex items-end">
        <img src={resolveImage(c.hero_image)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: ((c.hero_image_opacity ?? 100) / 100) }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pb-14 w-full">
          <div
            className="inline-block border-l-2 border-[#E60000] rounded-r-lg px-6 py-5 shadow-xl shadow-black/15"
            style={heroCardStyle(c)}
          >
            <p className="inline-flex items-center gap-3 text-xs md:text-sm font-bold tracking-[0.25em] uppercase text-white">
              <span className="inline-block w-8 h-px bg-[#E60000]" />
              {c.hero_breadcrumb}
            </p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] text-white mt-3">{c.hero_title}</h1>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{c.qui_title}</h2>
            <div className="w-16 h-0.5 bg-[#E60000] mx-auto mt-6 mb-10" />
            <div className="space-y-6 text-gray-700 leading-relaxed">
              {(c.qui_paragraphs || []).map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 120} className="text-center">
                <div data-testid={`value-${(v.title || "v").toLowerCase().replace(/\s/g, "")}`} className="group">
                  <div className="mx-auto w-16 h-16 rounded-full border border-gray-300 flex items-center justify-center mb-5 group-hover:border-[#E60000] transition-colors duration-500">
                    <Icon name={v.icon} className="w-7 h-7 text-[#1A1A1A] group-hover:text-[#E60000] transition-colors duration-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-base font-semibold tracking-tight mb-1.5">{v.title}</h3>
                  <p className="text-xs text-gray-600 leading-relaxed max-w-[200px] mx-auto">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-wide uppercase">{c.team_title}</h2>
            <div className="w-16 h-0.5 bg-[#E60000] mx-auto mt-6" />
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {TEAM.map((m, i) => (
              <Reveal key={`${m.name}-${i}`} delay={i * 150} data-testid={`team-${(m.name || "m").toLowerCase()}`} className="text-center">
                <div className="aspect-[4/5] overflow-hidden bg-gray-200 mb-5 group">
                  <img src={resolveImage(m.photo || m.img)} alt={m.name} className="img-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight">{m.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{m.role}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A1A1A] text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
          {stats.map((st, i) => (
            <Reveal key={i} delay={i * 120} className="flex items-center gap-5 justify-center lg:justify-start">
              <Icon name={st.icon} className="w-10 h-10 text-[#E60000] shrink-0" strokeWidth={1.25} />
              <div>
                <div className="font-display text-4xl md:text-5xl font-bold leading-none mb-1.5">
                  {st.value_text ? (
                    <span>{st.value_text}</span>
                  ) : (
                    <><CountUp end={Number(st.value) || 0} />{st.suffix || ""}</>
                  )}
                </div>
                <div className="overline text-gray-400">{st.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <SeoBlock title={c.seo_title} sections={c.seo_sections || []} />

      <section className="bg-[#1A1A1A] text-white border-t border-white/10 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight">{c.cta_title}</h3>
            <p className="text-sm text-gray-400 mt-2">{c.cta_text}</p>
          </div>
          <Link to="/contact" data-testid="entreprise-cta-contact" className="inline-flex items-center gap-3 bg-[#E60000] hover:bg-[#B30000] text-white px-7 py-4 text-sm font-medium tracking-wide uppercase transition-colors w-fit">
            {c.cta_button} <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </div>
  );
}
